"use client";

import Img from '@/components/Img';
import React, { useState, useEffect } from "react";
import CityPicker from "@/components/CityPicker";
import { useToast } from "@/components/ui/Toast";
import PhoneInput from "@/components/PhoneInput";
import { SuggestInput, type Suggestion } from "@/components/ui/SuggestInput";
import { track } from "@/lib/metrika";

// Конфиг страницы юр-лиц из админки (city_pages slug=autopilot, page=corporate;
// content_ru/en — JSON). Все поля опциональны: при пустом значении берётся
// статичный дефолт (страница 1:1 со Солнышко). Иконки — пути в /images/...
type CorpCard = { icon: string; title: string; desc: string };
type CorpStep = { icon: string; text: string };
type CorpCheckItem = { icon: string; text: string };
export interface CorpConfig {
  hero_title?: string; hero_sub?: string;
  steps?: CorpStep[];
  comfort_title?: string; comfort_text?: string;
  formats?: CorpCard[]; advantages?: CorpCard[];
  docs_title?: string; docs_subtitle?: string; docs_illustration?: string; docs_items?: CorpCheckItem[];
  options_title?: string; options_subtitle?: string; options_illustration?: string; options_items?: CorpCheckItem[];
  cta_text?: string; form_title?: string; form_desc?: string;
}

interface Props {
  locale: string;
  station?: string;
  // Города Автопилота для выпадающего списка «Город». Заявка уходит в
  // Telegram-группу ВЫБРАННОГО города (по его station).
  cities?: { station: string; name: string }[];
  // Контент страницы из админки. Пустые поля → статичные дефолты.
  config?: CorpConfig;
}

interface FormData {
  company: string;
  inn: string;
  contact_person: string;
  phone: string;
  city: string;     // имя выбранного города (для текста заявки)
  station: string;  // station выбранного города (определяет TG-группу)
  comment: string;
}

function createFormData(): FormData {
  return { company: "", inn: "", contact_person: "", phone: "", city: "", station: "", comment: "" };
}

const PRIMARY = "#0039A6";
const PRIMARY_HOVER = "#002A7A";
const GRAY_BG = "#EFF1F7";
const DOCS_BG = "#EAF0FB";
const OPTIONS_BG = "#E2E6EB";
const IMG = "/rent-site-img/common/yur";

export default function CorporatePageClient({ locale, station = "", cities = [], config = {} }: Props) {
  const isEn = locale === "en";
  const toast = useToast();
  // Хелперы: берём значение из конфига админки, иначе — статичный дефолт.
  const txt = (v: string | undefined, fallback: string) => (v && v.trim() ? v : fallback);
  const arr = <T,>(v: T[] | undefined, fallback: T[]) => (v && v.length ? v : fallback);

  const [heroForm, setHeroForm] = useState<FormData>(createFormData());
  const [bottomForm, setBottomForm] = useState<FormData>(createFormData());
  const [heroDial, setHeroDial] = useState("");
  const [bottomDial, setBottomDial] = useState("");
  const [heroSending, setHeroSending] = useState(false);
  const [bottomSending, setBottomSending] = useState(false);
  const [heroHover, setHeroHover] = useState(false);
  const [bottomHover, setBottomHover] = useState(false);

  const handleSubmit = async (formType: "hero" | "bottom") => {
    const form = formType === "hero" ? heroForm : bottomForm;
    const dial = formType === "hero" ? heroDial : bottomDial;
    const setSending = formType === "hero" ? setHeroSending : setBottomSending;

    if (!form.company || !form.inn || !form.contact_person || !form.phone || !form.station) {
      toast.show(isEn
        ? "Please fill in required fields: company, INN, contact person, phone and city"
        : "Заполните обязательные поля: компания, ИНН, контактное лицо, телефон и город", "error");
      return;
    }

    setSending(true);
    try {
      const fullPhone = `${dial} ${form.phone}`.trim();
      // Заявка уходит в TG-группу выбранного города (form.station); если город
      // не выбран — фолбэк на станцию по умолчанию (страховка, в норме не нужен).
      const res = await fetch("/api/v1/b2b-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, phone: fullPhone, station: form.station || station, site: "autopilot" }),
      });
      if (!res.ok) throw new Error("Request failed");
      track("corporate_submit", { city: form.station || station });
      toast.show(isEn
        ? "Your request has been received, a manager will contact you shortly"
        : "Ваша заявка получена, менеджер свяжется с Вами в ближайшее время", "success");
      if (formType === "hero") setHeroForm(createFormData());
      else setBottomForm(createFormData());
    } catch {
      toast.show(isEn
        ? "Failed to send the request. Please try again later"
        : "Не удалось отправить заявку. Попробуйте позже", "error");
    } finally {
      setSending(false);
    }
  };

  // Адаптив: десктоп 1:1 со Солнышко, мобайл — компактные паддинги/сетки.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const PAD_BLOCK = isMobile ? "44px 20px" : "80px 95px";
  const PAD_SIDE = isMobile ? "0 20px" : "0 95px";
  const PAD_CARD = isMobile ? "32px 20px" : "60px 95px";
  const COLS3 = isMobile ? "1fr" : "repeat(3, 1fr)";
  const COLS2 = isMobile ? "1fr" : "1fr 1fr";
  const FLEX_GAP = isMobile ? 28 : 60;

  /* ── Shared styles ── */
  const sectionTitle: React.CSSProperties = {
    fontSize: isMobile ? 28 : 40,
    fontWeight: "bold",
    color: "#000",
    marginBottom: isMobile ? 28 : 50,
    textAlign: "left",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    border: "none",
    borderBottom: "1px solid #e0e0e0",
    padding: "14px 0",
    fontSize: 16,
    outline: "none",
    background: "transparent",
    fontFamily: "inherit",
  };

  const submitBtnBase: React.CSSProperties = {
    background: PRIMARY,
    border: "none",
    borderRadius: 40,
    padding: "14px 40px",
    fontSize: 16,
    fontWeight: "bold",
    color: "#fff",
    textTransform: "uppercase",
    cursor: "pointer",
    transition: "background 0.2s",
    width: "100%",
    marginTop: 15,
  };

  /* ── Form renderer ── */
  const renderForm = (
    form: FormData,
    setForm: React.Dispatch<React.SetStateAction<FormData>>,
    setDial: React.Dispatch<React.SetStateAction<string>>,
    sending: boolean,
    onSubmit: () => void,
    hover: boolean,
    setHover: React.Dispatch<React.SetStateAction<boolean>>,
    layout: "vertical" | "grid" = "vertical"
  ) => {
    // Lookup a company by INN via the gateway proxy; fills the company name.
    // Город НЕ автозаполняем — он выбирается из выпадающего списка (определяет
    // TG-группу), а DaData-город не сопоставить со station напрямую.
    const fillFromINN = async (inn: string) => {
      const digits = inn.replace(/\D/g, "");
      if (digits.length !== 10 && digits.length !== 12) return;
      try {
        const r = await fetch("/api/v1/dadata/party/inn", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ inn: digits }),
        });
        if (!r.ok) return;
        const data = await r.json().catch(() => null);
        const hit = data?.suggestions?.[0];
        if (!hit) return;
        const companyName = (hit.value || "").toString();
        setForm((prev) => ({
          ...prev,
          company: prev.company || companyName,
        }));
      } catch {}
    };

    // ФИО-only filter (latin/cyrillic + spaces/hyphens/dots).
    const fioFilter = (raw: string) => raw.replace(/[^A-Za-zА-Яа-яЁё\s\-.]/g, "");
    // ИНН-only filter (digits, up to 12).
    const innFilter = (raw: string) => raw.replace(/\D/g, "").slice(0, 12);

    const fieldCompany = (
      <SuggestInput
        key="company"
        kind="party"
        value={form.company}
        onChange={(v) => setForm((prev) => ({ ...prev, company: v }))}
        onPick={(s: Suggestion) => {
          const innV = ((s.data as { inn?: string })?.inn) || "";
          setForm((prev) => ({
            ...prev,
            company: s.value,
            inn: prev.inn || innV,
          }));
        }}
        placeholder={isEn ? "Company name" : "Название юр. лица"}
        inputStyle={inputStyle}
      />
    );

    const fieldINN = (
      <input
        key="inn"
        value={form.inn}
        onChange={(e) => {
          const v = innFilter(e.target.value);
          setForm((prev) => ({ ...prev, inn: v }));
          fillFromINN(v);
        }}
        placeholder={isEn ? "Tax ID (INN)" : "ИНН"}
        inputMode="numeric"
        style={inputStyle}
      />
    );

    const fieldContact = (
      <SuggestInput
        key="contact_person"
        kind="fio"
        value={form.contact_person}
        onChange={(v) => setForm((prev) => ({ ...prev, contact_person: v }))}
        onPick={(s: Suggestion) => setForm((prev) => ({ ...prev, contact_person: s.value }))}
        filter={fioFilter}
        placeholder={isEn ? "Contact person" : "Контактное лицо"}
        inputStyle={inputStyle}
      />
    );

    const fieldCity = (
      <CityPicker
        key="city"
        cities={cities}
        station={form.station}
        onPick={(st, name) => setForm((prev) => ({ ...prev, station: st, city: name }))}
        isEn={isEn}
        inputStyle={inputStyle}
      />
    );

    const fieldComment = (
      <textarea
        key="comment"
        value={form.comment}
        onChange={(e) => setForm((prev) => ({ ...prev, comment: e.target.value }))}
        placeholder={isEn ? "Comment" : "Комментарий"}
        rows={1}
        style={{ ...inputStyle, resize: "none" as const, fontFamily: "inherit" }}
      />
    );

    const phoneField = (
      <div key="phone" style={{ padding: 0, borderBottom: "1px solid #e0e0e0", height: 48, display: "flex", alignItems: "center" }}>
        <PhoneInput
          value={form.phone}
          onChange={(p, d) => { setForm((prev) => ({ ...prev, phone: p })); setDial(d); }}
          height={44}
          fontSize={16}
          flat
        />
      </div>
    );

    const fields = [fieldCompany, fieldINN, fieldContact, phoneField, fieldCity, fieldComment];

    if (layout === "grid") {
      return (
        <>
          <div style={{ display: "grid", gridTemplateColumns: COLS2, columnGap: 40, rowGap: 0, alignItems: "end" }}>
            {fields[0]}{fields[3]}
            {fields[1]}{fields[4]}
            {fields[2]}{fields[5]}
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
            <button
              type="button"
              onClick={onSubmit}
              disabled={sending}
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
              style={{
                ...submitBtnBase,
                width: "auto",
                padding: "14px 60px",
                background: hover ? PRIMARY_HOVER : PRIMARY,
                opacity: sending ? 0.6 : 1,
              }}
            >
              {isEn ? "Get a commercial offer" : "Получить коммерческое предложение"}
            </button>
          </div>
        </>
      );
    }

    return (
      <>
        {fields}
        <button
          type="button"
          onClick={onSubmit}
          disabled={sending}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            ...submitBtnBase,
            background: hover ? PRIMARY_HOVER : PRIMARY,
            opacity: sending ? 0.6 : 1,
          }}
        >
          {isEn ? "Get a commercial offer" : "Получить коммерческое предложение"}
        </button>
      </>
    );
  };

  /* ── Steps data ── */
  const steps = arr<CorpStep>(config.steps, [
    { icon: `${IMG}/mdi_contract-outline.svg`, text: isEn ? "Submit a request" : "Оставьте заявку" },
    { icon: `${IMG}/material-symbols_contract-rounded.svg`, text: isEn ? "We agree on terms and send the contract" : "Согласуем условия и отправим договор" },
    { icon: `${IMG}/icon-park_pay-code-two.svg`, text: isEn ? "Payment by bank transfer" : "Оплата по безналичному расчёту" },
    { icon: `${IMG}/mdi_car-arrow-right.svg`, text: isEn ? "Car delivered" : "Авто доставлен" },
  ]).map((s, i) => ({ icon: s.icon, num: i + 1, text: s.text }));

  /* ── Formats data ── */
  const formats = arr<CorpCard>(config.formats, [
    {
      icon: `${IMG}/icon-short.png`,
      title: isEn ? "Short-term rental" : "Краткосрочная аренда",
      desc: isEn ? "From 1 day" : "От 1 дня",
    },
    {
      icon: `${IMG}/icon-mid.png`,
      title: isEn ? "Medium-term rental" : "Среднесрочная аренда",
      desc: isEn ? "From 7 to 30 days" : "От 7 до 30 дней",
    },
    {
      icon: `${IMG}/icon-long.png`,
      title: isEn ? "Long-term rental" : "Долгосрочная аренда",
      desc: isEn ? "From 1 month" : "От 1 месяца",
    },
    {
      icon: `${IMG}/icon-project.png`,
      title: isEn ? "Project rental" : "Проектная аренда",
      desc: isEn ? "For business objects and tasks" : "Под объекты и задачи бизнеса",
    },
    {
      icon: `${IMG}/icon-assign.png`,
      title: isEn ? "Vehicle assignment to employees" : "Закрепление авто за сотрудниками",
      desc: "",
    },
  ]);

  /* ── Advantages data ── */
  const advantages = arr<CorpCard>(config.advantages, [
    {
      icon: `${IMG}/icon-contract.png`,
      title: isEn ? "Framework agreement" : "Рамочный договор",
      desc: isEn
        ? "Sign one contract — rent cars at all company branches without repeated approvals."
        : "Подписываете один договор — арендуете авто во всех филиалах компании без повторных согласований.",
    },
    {
      icon: `${IMG}/icon-vat.png`,
      title: isEn ? "VAT included" : "Работа с НДС",
      desc: isEn
        ? "We provide a full set of accounting documents: invoices, acts, closing forms."
        : "Предоставляем полный пакет бухгалтерских документов: счета, акты, закрывающие формы.",
    },
    {
      icon: `${IMG}/icon-cars.png`,
      title: isEn ? "Cars no older than 3 years" : "Автомобили не старше 3 лет",
      desc: isEn
        ? "Modern, technically sound vehicles for reliable corporate trips."
        : "Современные, технически исправные автомобили для надежных корпоративных поездок.",
    },
    {
      icon: `${IMG}/icon-callcenter.png`,
      title: isEn ? "Dedicated call center line" : "Отдельная линия колл-центра",
      desc: isEn
        ? "A dedicated service channel for corporate clients, prompt communication and priority support."
        : "Выделенный канал обслуживания юридических клиентов, оперативная связь и приоритетное сопровождение.",
    },
  ]);

  /* ── Docs checklist ── */
  const docs = arr<CorpCheckItem>(config.docs_items, [
    { icon: `${IMG}/check-doc-1.png`, text: isEn ? "Rental agreement" : "Договор аренды" },
    { icon: `${IMG}/check-doc-2.png`, text: isEn ? "Acceptance and transfer acts" : "Акты приёма-передачи" },
    { icon: `${IMG}/check-doc-3.png`, text: isEn ? "Invoices for payment" : "Счета на оплату" },
    { icon: `${IMG}/check-doc-4.png`, text: isEn ? "Acts of services rendered" : "Акты выполненных услуг" },
    { icon: `${IMG}/check-doc-5.png`, text: isEn ? "Additional agreements" : "Дополнительные соглашения" },
  ]);

  /* ── Options checklist ── */
  const options = arr<CorpCheckItem>(config.options_items, [
    { icon: `${IMG}/check-opt-1.png`, text: isEn ? "Extended insurance coverage" : "Расширенное страховое покрытие" },
    { icon: `${IMG}/check-opt-2.png`, text: isEn ? "Second driver" : "Второй водитель" },
    { icon: `${IMG}/check-opt-3.png`, text: isEn ? "Additional equipment" : "Дополнительное оборудование" },
    { icon: `${IMG}/check-opt-4.png`, text: isEn ? "Replacement vehicle" : "Подменный автомобиль" },
    { icon: `${IMG}/check-opt-5.png`, text: isEn ? "Increased mileage limits" : "Увеличенные лимиты пробега" },
  ]);

  /* ── Checklist renderer ── */
  const renderChecklist = (items: { icon: string; text: string }[]) => (
    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
      {items.map((item, i) => (
        <li
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            padding: "10px 0",
            fontSize: 18,
            color: "#000",
          }}
        >
          <Img
            src={item.icon}
            alt=""
            style={{ width: 24, height: 24, marginRight: 12, flexShrink: 0 }}
          />
          <span>{item.text}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <div style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif", color: "#000" }}>
      {/* ════════ 1. HERO SECTION ════════ */}
      <section
        style={{
          backgroundImage: `url(${IMG}/hero-bg.png)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          padding: "80px 0",
          marginBottom: 60,
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 20px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: FLEX_GAP,
              flexWrap: "wrap",
            }}
          >
            {/* Left: text */}
            <div style={{ flex: 1, minWidth: 300 }}>
              <h1 style={{ fontSize: isMobile ? 30 : 40, fontWeight: "bold", color: "#000", marginBottom: 30, lineHeight: 1.2 }}>
                {txt(config.hero_title, isEn
                  ? "Car rental for legal entities and corporate clients"
                  : "Аренда автомобилей для юридических лиц и корпоративных клиентов")}
              </h1>
              <p style={{ fontSize: 22, color: "#000", maxWidth: 380, lineHeight: 1.5 }}>
                {txt(config.hero_sub, isEn
                  ? "Corporate car rental under contract — fast, convenient, with closing documents"
                  : "Корпоративная аренда автомобилей по договору — быстро, удобно, с закрывающими документами")}
              </p>
            </div>

            {/* Right: form */}
            <div
              style={{
                flex: "0 0 460px",
                background: "#fff",
                borderRadius: 40,
                padding: "37px 40px",
                boxShadow: "0px 10px 50px rgba(20, 29, 69, 0.1)",
                maxWidth: "100%",
              }}
            >
              <h3
                style={{
                  fontSize: 22,
                  fontWeight: "bold",
                  marginBottom: 20,
                  textAlign: "center",
                }}
              >
                {isEn ? "Leave a request" : "Оставьте заявку"}
              </h3>
              {renderForm(
                heroForm,
                setHeroForm,
                setHeroDial,
                heroSending,
                () => handleSubmit("hero"),
                heroHover,
                setHeroHover,
                "vertical"
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ════════ 2. STEPS SECTION ════════ */}
      <section style={{ padding: "15px 0 60px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 20px" }}>
          <h2 style={sectionTitle}>
            {isEn ? "How to arrange a rental" : "Как оформить аренду"}
          </h2>
          <div
            className="corp-steps"
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "center" : "flex-start",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: isMobile ? 28 : 10,
            }}
          >
            {steps.map((step, i) => (
              <React.Fragment key={i}>
                <div
                  className="corp-step"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    maxWidth: 200,
                  }}
                >
                  <div
                    style={{
                      width: 160,
                      height: 160,
                      borderRadius: "50%",
                      background: "#EAF0FB",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 8,
                    }}
                  >
                    <Img src={step.icon} alt="" style={{ width: 80, height: 80 }} />
                  </div>
                  <div
                    style={{
                      fontSize: 28,
                      fontWeight: "bold",
                      color: PRIMARY,
                      lineHeight: 1,
                    }}
                  >
                    {step.num}
                  </div>
                  <div style={{ fontSize: 15, color: "#000", textAlign: "center", marginTop: 6 }}>
                    {step.text}
                  </div>
                </div>
                {!isMobile && i < steps.length - 1 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "0 15px",
                      height: 160,
                    }}
                  >
                    <Img
                      src={`${IMG}/step-arrow.png`}
                      alt=""
                      style={{ width: 40 }}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ 3. MAIN BLOCK ════════ */}
      <section
        style={{
          maxWidth: 1200,
          margin: "0 auto 100px",
          backgroundColor: GRAY_BG,
          paddingBottom: 80,
          borderRadius: 40,
        }}
      >
        {/* Comfort */}
        <div
          style={{
            padding: PAD_BLOCK,
            marginBottom: 100,
            backgroundImage: `url(${IMG}/comfort-text-bg.png)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            borderRadius: "40px 40px 0 0",
            overflow: "hidden",
          }}
        >
          <h2 style={{ ...sectionTitle, textAlign: "left" }}>
            {txt(config.comfort_title, isEn ? "Convenient. Fast. Safe." : "Удобно. Быстро. Безопасно.")}
          </h2>
          <p style={{ fontSize: 18, color: "#000", maxWidth: 882, margin: 0 }}>
            {txt(config.comfort_text, isEn
              ? "We provide car rental to legal entities with a framework agreement, VAT support, our own fleet and a dedicated call center line for companies. Cars no older than 3 years, flexible rental formats and transparent service conditions."
              : "Предоставляем аренду автомобилей юридическим лицам с рамочным договором, поддержкой НДС, собственным автопарком и отдельной линией колл-центра для компаний. Авто не старше 3 лет, гибкие форматы аренды и прозрачные условия обслуживания.")}
          </p>
        </div>

        {/* Formats */}
        <div style={{ padding: PAD_SIDE }}>
          <h2 style={sectionTitle}>
            {isEn ? "Rental formats" : "Форматы аренды"}
          </h2>

          {/* Row 1: 3 cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: COLS3,
              gap: 34,
              marginBottom: 34,
            }}
          >
            {formats.slice(0, 3).map((f, i) => (
              <div
                key={i}
                style={{
                  background: "#fff",
                  borderRadius: 40,
                  padding: 30,
                  minHeight: 194,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Img src={f.icon} alt="" style={{ width: 50, height: 50 }} />
                <h4 style={{ fontSize: 22, fontWeight: "bold", marginTop: 15, marginBottom: 5 }}>
                  {f.title}
                </h4>
                <p style={{ fontSize: 16, color: "#000", margin: 0 }}>{f.desc}</p>
              </div>
            ))}
          </div>

          {/* Row 2: 2 cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: COLS3,
              gap: 34,
              marginBottom: 34,
            }}
          >
            {formats.slice(3).map((f, i) => (
              <div
                key={i}
                style={{
                  background: "#fff",
                  borderRadius: 40,
                  padding: 30,
                  minHeight: 194,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Img src={f.icon} alt="" style={{ width: 50, height: 50 }} />
                <h4 style={{ fontSize: 22, fontWeight: "bold", marginTop: 15, marginBottom: 5 }}>
                  {f.title}
                </h4>
                {f.desc && <p style={{ fontSize: 16, color: "#000", margin: 0 }}>{f.desc}</p>}
              </div>
            ))}
          </div>

          {/* Advantages */}
          <h2 style={{ ...sectionTitle, marginTop: 100 }}>
            {isEn ? "Advantages for legal entities" : "Преимущества для юридических лиц"}
          </h2>

          {/* Row 1: 2 cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: COLS2,
              gap: 30,
              marginBottom: 30,
            }}
          >
            {advantages.slice(0, 2).map((a, i) => (
              <div
                key={i}
                style={{
                  background: "#fff",
                  borderRadius: 40,
                  padding: "30px 40px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Img src={a.icon} alt="" style={{ width: 50, height: 50 }} />
                <h4 style={{ fontSize: 22, fontWeight: "bold", marginTop: 15, marginBottom: 10 }}>
                  {a.title}
                </h4>
                <p style={{ fontSize: 16, color: "#000", maxWidth: 330, marginBottom: 50 }}>
                  {a.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Row 2: 2 cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: COLS2,
              gap: 30,
              marginBottom: 30,
            }}
          >
            {advantages.slice(2).map((a, i) => (
              <div
                key={i}
                style={{
                  background: "#fff",
                  borderRadius: 40,
                  padding: "30px 40px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Img src={a.icon} alt="" style={{ width: 50, height: 50 }} />
                <h4 style={{ fontSize: 22, fontWeight: "bold", marginTop: 15, marginBottom: 10 }}>
                  {a.title}
                </h4>
                <p style={{ fontSize: 16, color: "#000", maxWidth: 330, marginBottom: 50 }}>
                  {a.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ 4. DOCUMENTS SECTION ════════ */}
      <div style={{ maxWidth: 1200, margin: "0 auto 100px", padding: "0 20px" }}>
        <h2 style={{ ...sectionTitle, marginBottom: 30 }}>
          {txt(config.docs_title, isEn ? "Documents and accounting" : "Документы и бухгалтерия")}
        </h2>
        <div
          style={{
            backgroundColor: DOCS_BG,
            borderRadius: 40,
            padding: PAD_CARD,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: FLEX_GAP, flexWrap: "wrap" }}>
            <div style={{ flex: "0 0 auto" }}>
              <Img
                src={txt(config.docs_illustration, `${IMG}/docs-illustration.png`)}
                alt=""
                style={{ maxWidth: 221, display: "block", margin: "0 auto" }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 280 }}>
              <h4 style={{ fontSize: 22, fontWeight: "bold", marginBottom: 25 }}>
                {txt(config.docs_subtitle, isEn
                  ? "We prepare a complete set of documents for corporate requirements:"
                  : "Готовим полный комплект документов под корпоративные требования:")}
              </h4>
              {renderChecklist(docs)}
            </div>
          </div>
        </div>
      </div>

      {/* ════════ 5. OPTIONS SECTION ════════ */}
      <div style={{ maxWidth: 1200, margin: "0 auto 100px", padding: "0 20px" }}>
        <h2 style={{ ...sectionTitle, marginBottom: 30 }}>
          {txt(config.options_title, isEn ? "Additional options" : "Дополнительные опции")}
        </h2>
        <div
          style={{
            backgroundColor: OPTIONS_BG,
            borderRadius: 40,
            padding: PAD_CARD,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: FLEX_GAP, flexWrap: "wrap" }}>
            <div style={{ flex: "0 0 auto" }}>
              <Img
                src={txt(config.options_illustration, `${IMG}/options-illustration.png`)}
                alt=""
                style={{ maxWidth: 221, display: "block", margin: "0 auto" }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 280 }}>
              <h4 style={{ fontSize: 22, fontWeight: "bold", marginBottom: 25 }}>
                {txt(config.options_subtitle, isEn ? "Extended service package" : "Расширенный пакет услуг")}
              </h4>
              {renderChecklist(options)}
            </div>
          </div>
        </div>
      </div>

      {/* ════════ 6. CTA BLOCK ════════ */}
      <section style={{ padding: "80px 0", backgroundColor: OPTIONS_BG, margin: "60px 0" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 20px" }}>
          <p
            style={{
              fontSize: isMobile ? 22 : 30,
              fontWeight: "bold",
              color: "#000",
              maxWidth: 1060,
              marginBottom: 0,
              lineHeight: 1.4,
            }}
          >
            {txt(config.cta_text, isEn
              ? "Corporate car rental for legal entities with transparent conditions, framework agreements and service adapted to business needs."
              : "Корпоративная аренда автомобилей для юридических лиц с прозрачными условиями, рамочными договорами и сервисом, адаптированным под бизнес-задачи.")}
          </p>
        </div>
      </section>

      {/* ════════ 7. BOTTOM FORM ════════ */}
      <div style={{ maxWidth: 1200, margin: "0 auto 100px", padding: "0 20px" }}>
        <div
          style={{
            backgroundColor: OPTIONS_BG,
            borderRadius: 40,
            padding: PAD_BLOCK,
          }}
        >
          <h2 style={{ ...sectionTitle, textAlign: "left" }}>
            {txt(config.form_title, isEn ? "Leave a request" : "Оставьте заявку")}
          </h2>
          <p
            style={{
              fontSize: 18,
              color: "#000",
              maxWidth: 882,
              marginBottom: 40,
              textAlign: "left",
            }}
          >
            {txt(config.form_desc, isEn
              ? "We provide car rental to legal entities with a framework agreement, VAT support, our own fleet and a dedicated call center line for companies. Cars no older than 3 years, flexible rental formats and transparent service conditions."
              : "Предоставляем аренду автомобилей юридическим лицам с рамочным договором, поддержкой НДС, собственным автопарком и отдельной линией колл-центра для компаний. Авто не старше 3 лет, гибкие форматы аренды и прозрачные условия обслуживания.")}
          </p>
          <div
            style={{
              background: "#fff",
              borderRadius: 40,
              padding: 40,
              boxShadow: "0px 10px 50px rgba(20, 29, 69, 0.1)",
            }}
          >
            {renderForm(
              bottomForm,
              setBottomForm,
              setBottomDial,
              bottomSending,
              () => handleSubmit("bottom"),
              bottomHover,
              setBottomHover,
              isMobile ? "vertical" : "grid"
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
